/**
 * Modern (v4) UIKit chat selectors — internal implementations.
 *
 * These are consumed by the facade selectors in `@/domain/chat/chatSelector.js`.
 * Do not import directly from components — use the facades instead.
 */
import { Map } from 'immutable';
import { selector } from '@/lib/selectors';
// eslint-disable-next-line no-restricted-syntax
import { loadedDataCompanyIdSelector } from '@/domain/documents/documentsLeafSelectors';

const chat = (state) => state.chat;
const documents = (state) => state.documents;

// Chat follows the company whose data the store holds, not the one in the URL: it keeps the
// last company on pages that name none, and waits out a company switch until the new
// company's chat users have loaded. A known compromise, to revisit together with the profile.

// V4: derives from chat.unreadDocuments (Immutable.Map<companyId, Immutable.Map<documentId, count>>).
// Returns the current company's per-document unread map. Presence of a documentId (Map.has)
// means it has unread; its value is the unread message count for that document.
export const modernDocumentsUnreadSelector = selector(
  chat,
  loadedDataCompanyIdSelector,
  (c, companyId) => c.get('unreadDocuments').get(companyId) || Map(),
);

// V4: unread message count for the CURRENTLY OPEN document — the sum of unread across all
// channels carrying this documentId (already summed into unreadDocuments by companyId+documentId).
export const modernCurrentDocumentUnreadSelector = selector(
  chat,
  loadedDataCompanyIdSelector,
  documents,
  (c, companyId, d) => c.get('unreadDocuments').getIn([companyId, d.document.documentID]) || 0,
);

// V4: derives from chat.unreadCompanies (Immutable.Map<companyId, count>)
export const modernCompanyUnreadSelector = selector(chat, (c) => c.get('unreadCompanies') || Map());

// V4: reads from chat.totalUnreadCount populated by REST + WS push
export const modernTotalUnreadSelector = selector(chat, (c) => c.get('totalUnreadCount') || 0);

// V4: extracts unread count for the current companyId from unreadCompanies map
export const currentCompanyUnreadSelector = selector(
  chat,
  loadedDataCompanyIdSelector,
  (c, companyId) => (c.get('unreadCompanies') || Map()).get(companyId) || 0,
);
